<template>
  <WidgetCadre
    titre="Notifications"
    v-bind="apparence"
    :lien="donnees?.lien || '/notifications'"
    libelle-lien="Historique"
    :chargement="chargement"
    :panne="panne"
    :reglable="reglable"
    @retirer="$emit('retirer')"
    @recharger="$emit('recharger')"
  >
    <template #indice>
      <span
        v-if="nonLues"
        class="notifs__compte"
      >{{ nonLues }} non {{ nonLues > 1 ? 'lues' : 'lue' }}</span>
    </template>

    <div
      v-if="lignes.length"
      class="notifs surface"
    >
      <NotificationItem
        v-for="notification in lignes"
        :key="notification.id"
        :notification="notification"
        dense
        @open="ouvrir(notification)"
        @toggle-read="basculer(notification)"
      />
    </div>

    <p
      v-else
      class="etiquette"
    >
      Aucune notification pour le moment. Elles arrivent ici quand un ticket,
      une tâche ou un traitement vous concerne.
    </p>
  </WidgetCadre>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { ElMessage } from 'element-plus'
import NotificationItem from '@/components/Notifications/NotificationItem.vue'
import WidgetCadre from './WidgetCadre.vue'
import { apparenceDe } from './apparence'

/**
 * Les dernières notifications, rendues par la même ligne que la cloche.
 *
 * Les changements d'état passent par les actions du magasin, comme dans la
 * cloche et l'historique : ce sont elles qui tiennent le badge à jour. Un
 * « marquer lu » fait d'ici qui laisserait la cloche afficher l'ancien compte
 * dirait deux choses différentes sur le même écran.
 */

const props = defineProps({
    donnees: { type: Object, default: null },
    chargement: { type: Boolean, default: false },
    panne: { type: Boolean, default: false },
    reglable: { type: Boolean, default: false },
})

defineEmits(['retirer', 'recharger'])

const apparence = apparenceDe('notifications')
const router = useRouter()
const store = useStore()

/** Copie locale : l'état lu / non lu change ici sans attendre un rechargement du widget. */
const lignes = ref([])

watch(
    () => props.donnees?.dernieres,
    dernieres => { lignes.value = (dernieres ?? []).map(n => ({ ...n })) },
    { immediate: true },
)

const nonLues = computed(() => {
    const serveur = props.donnees?.non_lues ?? 0
    const ecartLocal = lignes.value.filter(n => !n.read_at).length
        - (props.donnees?.dernieres ?? []).filter(n => !n.read_at).length

    return Math.max(serveur + ecartLocal, 0)
})

async function basculer(notification) {
    try {
        const reponse = await store.dispatch(
            notification.read_at ? 'notifications/markUnread' : 'notifications/markRead',
            notification.id,
        )

        notification.read_at = reponse?.data?.read_at ?? null
    } catch {
        ElMessage.warning("L'état de la notification n'a pas pu être modifié.")
    }
}

async function ouvrir(notification) {
    if (!notification.read_at) await basculer(notification)

    const url = notification.data?.action_url || notification.data?.url

    if (url) router.push(url)
}
</script>

<style lang="scss" scoped>
.notifs {
  overflow: hidden;

  :deep(.notif-item:last-child) { border-bottom: none; }

  &__compte {
    padding: 2px 9px;
    border-radius: 999px;
    background: var(--accent);
    color: #fff;
    font-size: 11.5px;
    font-weight: 600;
    white-space: nowrap;
  }
}
</style>
